"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  ARMENIAN_SYSTEMS,
  type ArmenianOptions,
  type ArmenianSystem,
  armenianLetterRows,
  latinToArmenian,
  romanizeArmenian,
} from "@/lib/tool-kit/transliteration-more/armenian";

/** English strings. A translated content file overrides these through `ui`. */
export const ARMENIAN_TRANSLITERATOR_STRINGS = {
  direction: "Direction",
  toLatin: "Armenian → Latin",
  toArmenian: "Latin → Armenian",
  inputLabel: "Armenian text",
  inputHint:
    "Eastern or Western Armenian, in the modern or the Classical orthography. Armenian punctuation is converted too.",
  latinInputLabel: "Latin text",
  latinInputHint:
    "ISO 9985 or ALA-LC. Aspirates can be typed with ʻ, ʼ, ' or a backtick, and an ALA-LC prime with ʹ or '.",
  system: "System to read",
  samples: {
    one: "Հայաստան և Երևան",
    two: "Դու կարդացե՞լ ես այս գիրքը",
    iso: "Hayastan ew Erewan",
    "ala-lc": "Hayastan ev Erevan",
  },
  sampleOne: "Sample",
  sampleTwo: "Sample with a question",
  sampleLatin: "Insert a sample",
  optionsLegend: "ALA-LC options",
  western: "Western Armenian values: b↔p, g↔k, d↔t, ts↔dz, ch↔j",
  classical:
    "Classical orthography: initial ե before a vowel is y, initial յ is ḥ, և is ew",
  resultsHeading: "Romanized",
  armenianHeading: "Armenian",
  systems: {
    iso: "ISO 9985:1996",
    "ala-lc": "ALA-LC (Library of Congress)",
  },
  uses: {
    iso: "One Latin letter per Armenian letter, no digraphs, so it can be reversed exactly. For linguistics and bibliographic exchange.",
    "ala-lc":
      "Library catalogs and most English-language reference lists. Digraphs and a prime between letters that could be misread.",
  },
  armenianUse: "Armenian written from the {system} text above.",
  enterText: "Type or paste some text to convert.",
  unknownChars:
    "Left unchanged because the table does not include them: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  letterColumn: "Letter",
  isoColumn: "ISO 9985",
  eastColumn: "ALA-LC Eastern",
  westColumn: "ALA-LC Western (if different)",
  tableNote:
    "ALA-LC writes the Eastern and Classical values by default and gives the Western ones in brackets. The aspirate sign is ʻ in ALA-LC and ʼ in ISO 9985, and a prime ʹ goes between two letters that would otherwise read as a digraph.",
} as const;

type Strings = typeof ARMENIAN_TRANSLITERATOR_STRINGS;
type Direction = "to-latin" | "to-armenian";

const DEFAULT_OPTIONS: ArmenianOptions = { western: false, classical: false };

function LetterTable({ strings }: { strings: Strings }) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <DataTable
        head={[
          strings.letterColumn,
          strings.isoColumn,
          strings.eastColumn,
          strings.westColumn,
        ]}
        rows={armenianLetterRows().map((row) => [
          row.armenian,
          row.iso,
          row.east,
          row.west,
        ])}
      />
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function ToLatinResults({
  text,
  options,
  strings,
}: {
  text: string;
  options: ArmenianOptions;
  strings: Strings;
}) {
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {ARMENIAN_SYSTEMS.map((system) => (
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            key={system}
            lang="hy-Latn"
            text={romanizeArmenian(text, system, options).text}
            title={strings.systems[system]}
            use={strings.uses[system]}
          />
        ))}
      </div>
    </section>
  );
}

function ToArmenianResults({
  text,
  system,
  options,
  strings,
}: {
  text: string;
  system: ArmenianSystem;
  options: ArmenianOptions;
  strings: Strings;
}) {
  const result = latinToArmenian(text, system, options);
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.armenianHeading}</h2>
      {result.unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownChars, { chars: result.unknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang="hy"
        text={result.text}
        title={strings.systems[system]}
        use={t(strings.armenianUse, { system: strings.systems[system] })}
      />
    </section>
  );
}

export default function ArmenianTransliteratorWidget() {
  const strings = useToolUi(ARMENIAN_TRANSLITERATOR_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-latin");
  const [system, setSystem] = useState<ArmenianSystem>("ala-lc");
  const [text, setText] = useState<string>(strings.samples.one);
  const [latin, setLatin] = useState<string>(strings.samples["ala-lc"]);
  const [options, setOptions] = useState<ArmenianOptions>(DEFAULT_OPTIONS);
  const forward = direction === "to-latin";
  const current = forward ? text : latin;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="hy-direction"
        onChange={setDirection}
        options={[
          { value: "to-latin", label: strings.toLatin },
          { value: "to-armenian", label: strings.toArmenian },
        ]}
        value={direction}
      />
      {forward ? (
        <>
          <ScriptTextArea
            dir="ltr"
            hint={strings.inputHint}
            id="hy-input"
            label={strings.inputLabel}
            lang="hy"
            onChange={setText}
            value={text}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.one)}
              type="button"
            >
              {strings.sampleOne}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.two)}
              type="button"
            >
              {strings.sampleTwo}
            </button>
          </div>
        </>
      ) : (
        <>
          <PillGroup
            label={strings.system}
            name="hy-system"
            onChange={setSystem}
            options={ARMENIAN_SYSTEMS.map((each) => ({
              value: each,
              label: strings.systems[each],
            }))}
            value={system}
          />
          <ScriptTextArea
            dir="ltr"
            hint={strings.latinInputHint}
            id="hy-latin-input"
            label={strings.latinInputLabel}
            lang="hy-Latn"
            onChange={setLatin}
            value={latin}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setLatin(strings.samples[system])}
              type="button"
            >
              {strings.sampleLatin}
            </button>
          </div>
        </>
      )}
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.optionsLegend}</legend>
        <Checkbox
          checked={options.western}
          label={strings.western}
          onChange={(western) => setOptions((o) => ({ ...o, western }))}
        />
        <Checkbox
          checked={options.classical}
          label={strings.classical}
          onChange={(classical) => setOptions((o) => ({ ...o, classical }))}
        />
      </fieldset>
      {current.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {current.trim() !== "" && forward ? (
        <ToLatinResults options={options} strings={strings} text={text} />
      ) : null}
      {current.trim() !== "" && !forward ? (
        <ToArmenianResults
          options={options}
          strings={strings}
          system={system}
          text={latin}
        />
      ) : null}
      <LetterTable strings={strings} />
    </div>
  );
}
